import * as React from 'react'
import { DateTime } from 'luxon'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
import { post } from '../../helpers/apiHelper.ts'
import { getEventIcon, getEventName } from '../../helpers/eventHelper.tsx'
import { PET_EVENTS_URL } from '../../helpers/urls.ts'
import { effectiveTimezone } from '../../store/authState.ts'
import { useSnapshot } from 'valtio'
import { authState } from '../../store/authState.ts'
import { dataState } from '../../store/dataState.ts'
import { IDevice, IEvent, IPet } from '../../interfaces/types.ts'

interface Props {
    pet: IPet
    open: boolean
    onClose: () => void
    onViewAll?: () => void
    /** When true, show exact timestamp alongside relative time */
    showExactTime?: boolean
}

/**
 * Shows the last 5 events for a pet in a modal popup.
 * Fetched fresh each time it opens.
 */
export const RecentEventsPopup: React.FC<Props> = ({ pet, open, onClose, onViewAll, showExactTime }) => {
    const [events, setEvents] = React.useState<IEvent[]>([])
    const [loading, setLoading] = React.useState(false)
    const devices = (useSnapshot(dataState) as typeof dataState).devices
    useSnapshot(authState)
    const zone = effectiveTimezone().value

    React.useEffect(() => {
        if (open && pet) {
            void fetchRecent()
        }
    }, [open, pet?._id])

    const fetchRecent = async () => {
        setLoading(true)
        setEvents([])
        const device: IDevice | undefined = devices.find(d => d.petId === pet._id)
        try {
            const res = await post(PET_EVENTS_URL, {
                petId: pet._id,
                iccid: device?.iccid,
                startDate: DateTime.now().toISO()!,
                direction: 'back',
            })
            if (res?.status === 200 && res.data?.length) {
                // Take only the first 5
                setEvents(res.data.slice(0, 5))
            }
        } catch (e) {
            console.log('RecentEventsPopup fetch error', e)
        }
        setLoading(false)
    }

    /** Format time as relative ("2 min ago") or absolute if > 24h */
    const formatTime = (isoDate: string) => {
        const dt = DateTime.fromISO(isoDate).setZone(zone)
        const diff = DateTime.now().setZone(zone).diff(dt, ['hours', 'minutes'])
        const hours = Math.floor(diff.hours)
        const minutes = Math.floor(diff.minutes)

        if (hours < 1) {
            return minutes <= 1 ? 'Just now' : `${minutes} min ago`
        }
        if (hours < 24) {
            return hours === 1 ? '1 hour ago' : `${hours} hours ago`
        }
        return dt.toLocaleString(DateTime.DATETIME_MED)
    }

    if (!open) return null

    const content = (
        <div
            className={'fixed inset-0 z-[100000] flex items-center justify-center bg-black/60'}
            data-theme={'gibi'}
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        >
            <div className={`bg-base-100 rounded-xl shadow-2xl ${showExactTime ? 'w-[90vw] max-w-[480px]' : 'w-[90vw] max-w-[360px]'} max-h-[70vh] flex flex-col overflow-hidden`}>
                {/* Header */}
                <div className={'flex items-center justify-between px-4 py-3 border-b border-base-300'}>
                    <div className={'text-lg font-bold text-primary'}>
                        {pet.petName}'s Recent Events
                    </div>
                    <button
                        className={'btn btn-sm btn-ghost btn-circle text-primary'}
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                {/* Body */}
                <div className={'flex-1 overflow-y-auto'}>
                    {loading && (
                        <div className={'flex items-center justify-center py-8'}>
                            <span className="loading loading-spinner loading-md text-primary"></span>
                        </div>
                    )}

                    {!loading && events.length === 0 && (
                        <div className={'text-center py-8 text-base-content/60'}>
                            No recent events
                        </div>
                    )}

                    {!loading && events.map((event, i) => {
                        const name = getEventName(event)
                        const icon = getEventIcon(event)
                        const timeLabel = formatTime(event.createdAt)
                        const exactTime = showExactTime
                            ? DateTime.fromISO(event.createdAt).setZone(zone).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)
                            : ''

                        return (
                            <div
                                key={event._id}
                                className={`flex items-center gap-3 px-4 py-2.5 ${i % 2 === 0 ? 'bg-base-100' : 'bg-primary/5'}`}
                            >
                                <div className={'shrink-0 text-primary'}>{icon}</div>
                                <div className={'flex-1 min-w-0'}>
                                    <div className={'text-sm font-semibold text-base-content truncate'}>{name}</div>
                                    <div className={'text-xs text-base-content/60'}>
                                        {timeLabel}
                                        {showExactTime && exactTime && (
                                            <span className={'ml-2 opacity-70'}>· {exactTime}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* Footer — View All link */}
                {onViewAll && !loading && events.length > 0 && (
                    <div className={'border-t border-base-300 px-4 py-2'}>
                        <button
                            className={'btn btn-sm btn-ghost text-primary w-full'}
                            onClick={() => { onClose(); onViewAll() }}
                        >
                            View All Events
                        </button>
                    </div>
                )}
            </div>
        </div>
    )

    return createPortal(content, document.body)
}
